import * as React from "react";
import { Box, BoxProps, Text, theme } from "@aksara-ui/react";
import styled, { CSSObject } from "styled-components";

export type ActionListItemVariant = "default" | "destructive";
export interface ActionListItemProps
  extends Omit<BoxProps, "sx">,
    React.ComponentPropsWithoutRef<"div"> {
  className?: string;
  style?: React.CSSProperties;
  containerStyle?: CSSObject;
  contentStyle?: CSSObject;
  isActive?: boolean;
  children?: React.ReactNode;
  disabled?: boolean;
  variant?: ActionListItemVariant;
  indicator?: boolean;
}

const ActionListItemStyled = styled(Box)<{ isActive?: boolean }>`
  position: relative;
  overflow: hidden;
  height: auto;
  display: flex;
  align-items: stretch;
  border-radius: 8px;
  cursor: pointer;
  background-color: ${(props) =>
    props.isActive ? theme.colors.blue01 : theme.colors.grey01};
  color: ${(props) => (props.isActive ? theme.colors.blue07 : "#313F4E")};

  &:hover {
    background-color: ${(props) =>
      props.isActive ? theme.colors.blue01 : theme.colors.grey03};
    box-shadow: none;
  }
  &:active {
    background-color: ${theme.colors.blue02};
  }
  &:focus-visible {
    outline: none;
    padding: 0px;
    box-shadow: 0 0 0 2px rgba(175, 214, 255, 0.7);
  }
  &[data-disabled] {
    color: #b0b7bf;
    background-color: #ffffff;
    cursor: not-allowed;
  }
`;

const ActionListItem = React.forwardRef<HTMLDivElement, ActionListItemProps>(
  (
    {
      style,
      children,
      isActive,
      disabled,
      containerStyle,
      variant = "default",
      contentStyle,
      indicator = true,
      ...rest
    },
    ref
  ) => {
    const renderLabel = () => {
      if (typeof children === "string" || typeof children === "number") {
        return <Text scale={300}>{children}</Text>;
      }
      return children;
    };
    const disabledProps = disabled
      ? { "aria-disabled": true, "data-disabled": true }
      : {};
    return (
      <ActionListItemStyled
        {...disabledProps}
        ref={ref}
        style={style}
        isActive={isActive}
        {...rest}
      >
        {isActive && indicator && (
          // Indicator
          <Box
            width={4}
            position="absolute"
            left="0"
            top="0"
            height="100%"
            backgroundColor={theme.colors.blue07}
          />
        )}
        <Box py="xs" px="sm" sx={{ ...contentStyle }}>
          {renderLabel()}
        </Box>
      </ActionListItemStyled>
    );
  }
);

export default ActionListItem;
